import { check as checkFF } from '@webapp-next/lib/feature-flag';
import userHasPermission from '@webapp-next/lib/user-has-permission';

const PAY_SDP_FF = 'payx-2078-view-sensitive-employee-pay-info';

export function isPaySdpEnabled(): boolean {
  return checkFF(PAY_SDP_FF);
}

export interface PaySdpPermissions {
  /**
   * Sensitive data protection FF is ON.
   *
   * When false, callers should treat `hasAccess` / `shouldHide` as describing
   * the *effective* behavior of the UI under the current rollout (i.e. fields
   * are visible and treated as accessible), not the user's underlying pay
   * permissions.
   */
  sdpEnabled: boolean;
  /**
   * Whether fields should be hidden, given the current SDP configuration.
   *
   * When `sdpEnabled === true`, this is derived from the user's permissions:
   *   - true  => user lacks required permissions → hide fields entirely
   *   - false => user has the required permissions → show fields
   *
   * When `sdpEnabled === false`, this will always be false (fields are shown),
   * regardless of the user's underlying permissions.
   */
  shouldHide: boolean;
  /**
   * Whether the user has access to view sensitive pay data, *given the current
   * SDP configuration*.
   *
   * When `sdpEnabled === true`, this reflects the real permission state
   * (user has the required granting permissions).
   *
   * When `sdpEnabled === false`, this will always be true to represent the
   * effective behavior that the feature is not enforced, and therefore does
   * NOT indicate the user's underlying permission grants.
   */
  hasAccess: boolean;
}

/**
 * Schedule surfaces — AND logic between Can_View_Roster_Cost
 * and Can_View_Sensitive_Emp_Pay_Info when FF is ON.
 */
export function checkPaySdpSchedule(): PaySdpPermissions {
  const sdpEnabled = isPaySdpEnabled();
  if (!sdpEnabled) {
    return { sdpEnabled: false, shouldHide: false, hasAccess: true };
  }

  const hasAccess =
    userHasPermission('Can_View_Sensitive_Emp_Pay_Info') &&
    userHasPermission('Can_View_Roster_Cost');

  return {
    sdpEnabled: true,
    shouldHide: !hasAccess,
    hasAccess,
  };
}

/**
 * Non-schedule surfaces (People, Timesheets, Payroll) —
 * only Can_View_Sensitive_Emp_Pay_Info grants access.
 */
export function checkPaySdpGeneral(): PaySdpPermissions {
  const sdpEnabled = isPaySdpEnabled();
  if (!sdpEnabled) {
    return { sdpEnabled: false, shouldHide: false, hasAccess: true };
  }

  const hasAccess = userHasPermission('Can_View_Sensitive_Emp_Pay_Info');

  return {
    sdpEnabled: true,
    shouldHide: !hasAccess,
    hasAccess,
  };
}
